/**
 * Change Request Service
 * Handles API calls for investor change request workflow
 */

import { apiClient } from '../utils/api-client';
import { investorPortalApiClient } from '../portal/shared/utils/api-client';
import type {
  ChangeRequestCreate,
  ChangeRequestCreateResponse,
  ChangeRequestDetail,
  ChangeRequestListResponse,
  PortalChangeRequestList,
} from '../types/changeRequest';

class ChangeRequestService {
  private adminBaseUrl = '/investor-change-data';
  private portalBaseUrl = '/portal/investors';

  // Admin endpoints

  /**
   * Get pending change requests (admin)
   */
  async getPendingRequests(
    params: {
      investor_id?: string;
      skip?: number;
      limit?: number;
    } = {}
  ): Promise<ChangeRequestListResponse> {
    const { investor_id, skip = 0, limit = 100 } = params;

    const searchParams = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });

    if (investor_id) {
      searchParams.append('investor_id', investor_id);
    }

    return apiClient.get(
      `${this.adminBaseUrl}/pending?${searchParams.toString()}`
    );
  }

  /**
   * Get all change requests for a specific investor (admin)
   */
  async getInvestorRequests(
    investorId: string,
    params: { skip?: number; limit?: number } = {}
  ): Promise<ChangeRequestListResponse> {
    const { skip = 0, limit = 100 } = params;

    const searchParams = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });

    return apiClient.get(
      `${this.adminBaseUrl}/investor/${investorId}?${searchParams.toString()}`
    );
  }

  /**
   * Get change request details (admin)
   */
  async getChangeRequest(requestId: string): Promise<ChangeRequestDetail> {
    return apiClient.get(`${this.adminBaseUrl}/${requestId}`);
  }

  /**
   * Get count of pending change requests for a specific investor (admin)
   * Note: This method makes an API call. For displaying counts in tables,
   * prefer fetching all pending requests once and computing counts client-side.
   */
  async getPendingCountForInvestor(investorId: string): Promise<number> {
    const response = await this.getPendingRequests({
      investor_id: investorId,
    });
    return response.total;
  }

  /**
   * Approve a change request (admin)
   */
  async approveChangeRequest(
    requestId: string,
    reviewerNotes?: string
  ): Promise<ChangeRequestDetail> {
    const searchParams = new URLSearchParams();
    if (reviewerNotes) {
      searchParams.append('reviewer_notes', reviewerNotes);
    }

    return apiClient.post(
      `${this.adminBaseUrl}/${requestId}/approve?${searchParams.toString()}`,
      {}
    );
  }

  /**
   * Reject a change request (admin)
   */
  async rejectChangeRequest(
    requestId: string,
    rejectionReason: string,
    reviewerNotes?: string
  ): Promise<ChangeRequestDetail> {
    const searchParams = new URLSearchParams({
      rejection_reason: rejectionReason,
    });

    if (reviewerNotes) {
      searchParams.append('reviewer_notes', reviewerNotes);
    }

    return apiClient.post(
      `${this.adminBaseUrl}/${requestId}/reject?${searchParams.toString()}`,
      {}
    );
  }

  // Portal endpoints (investor contacts)

  /**
   * Submit a change data (investor portal)
   */
  async createChangeRequest(
    data: ChangeRequestCreate
  ): Promise<ChangeRequestCreateResponse> {
    return investorPortalApiClient.post(
      `${this.portalBaseUrl}/change-data`,
      data
    );
  }

  /**
   * Get my change requests (investor portal)
   */
  async getMyRequests(): Promise<PortalChangeRequestList> {
    return investorPortalApiClient.get(`${this.portalBaseUrl}/change-data`);
  }

  /**
   * Cancel a pending change request (investor portal)
   */
  async cancelChangeRequest(requestId: string): Promise<void> {
    return investorPortalApiClient.delete(
      `${this.portalBaseUrl}/requests/${requestId}`
    );
  }
}

export const changeRequestService = new ChangeRequestService();
